import React, { useEffect, useState } from 'react';
import { ChevronRightIcon, BookOpenIcon, DatabaseIcon, FilterIcon, RefreshCwIcon, SearchIcon, CalendarIcon, InfoIcon, AlertCircleIcon, XIcon } from 'lucide-react';
import { LedgerTable } from './LedgerTable';
import { LedgerEntryDetail } from './LedgerEntryDetail';
import { formatAmount } from '../utils/currencyFormatter';
// Define types for the operational ledger entry
export type DocumentType = 'Invoice' | 'Purchase Order' | 'Credit Note' | 'Expense Claim' | 'Contract' | 'Payment';
export type DocumentStatus = 'Draft' | 'Approved' | 'Exported' | 'Paid' | 'Cancelled' | 'Reversed';
export type LedgerState = 'Informational' | 'Active' | 'Reversed';
export type LedgerEntryType = 'Document' | 'Reversal' | 'Payment';
export type LedgerEntry = {
  // Identity and Document Context
  ledgerEntryId: string;
  createdDate: string;
  documentId: string;
  documentNumber: string;
  documentType: DocumentType;
  documentStatus: DocumentStatus;
  // Organisational Context
  orgUnitId: string;
  orgUnitName: string;
  orgUnitPath: string;
  // Financial Fields
  debitAmount: number | null;
  creditAmount: number | null;
  calculatedRunningBalance?: number;
  currencyCode: string;
  displayPrecision: number;
  // Coding and Allocation
  glCode: string;
  // Counterparty
  vendorName?: string;
  userName?: string;
  // Ledger Classification
  ledgerState: LedgerState;
  ledgerEntryType: LedgerEntryType;
  reversesLedgerEntryId?: string | null;
  // Audit and Traceability
  createdBy: string;
  sourceSystem: 'Rapid' | 'ERP';
  erpReference?: string | null;
  reasonOrNotes?: string;
};
export function LedgerView({
  locale = 'en-US'
}) {
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<LedgerEntry | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    documentType: [] as DocumentType[],
    documentStatus: [] as DocumentStatus[],
    ledgerState: [] as LedgerState[],
    orgUnitId: '',
    glCode: '',
    vendorOrUser: '',
    searchTerm: ''
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(true);
  // Fetch sample ledger data
  useEffect(() => {
    // Sample operational ledger data
    const sampleData: LedgerEntry[] = [{
      ledgerEntryId: 'OLE-2024-001',
      createdDate: '2024-01-15T10:30:00Z',
      documentId: 'INV-001',
      documentNumber: 'INV-2024-001',
      documentType: 'Invoice',
      documentStatus: 'Approved',
      orgUnitId: 'org-002',
      orgUnitName: 'New York Headquarters',
      orgUnitPath: 'Global Technologies Inc. > New York Headquarters',
      debitAmount: 1250.75,
      creditAmount: null,
      currencyCode: 'USD',
      displayPrecision: 2,
      glCode: '5000.1100.001',
      vendorName: 'Office Depot',
      ledgerState: 'Active',
      ledgerEntryType: 'Document',
      createdBy: 'John Smith',
      sourceSystem: 'Rapid',
      reasonOrNotes: 'Office supplies purchase'
    }, {
      ledgerEntryId: 'OLE-2024-002',
      createdDate: '2024-01-15T14:20:00Z',
      documentId: 'PO-001',
      documentNumber: 'PO-2024-002',
      documentType: 'Purchase Order',
      documentStatus: 'Approved',
      orgUnitId: 'org-002',
      orgUnitName: 'New York Headquarters',
      orgUnitPath: 'Global Technologies Inc. > New York Headquarters',
      debitAmount: 5430.0,
      creditAmount: null,
      currencyCode: 'USD',
      displayPrecision: 2,
      glCode: '1200.2100.005',
      vendorName: 'Dell Technologies',
      ledgerState: 'Active',
      ledgerEntryType: 'Document',
      createdBy: 'Jane Doe',
      sourceSystem: 'Rapid',
      reasonOrNotes: 'IT equipment purchase order'
    }, {
      ledgerEntryId: 'OLE-2024-003',
      createdDate: '2024-01-16T09:15:00Z',
      documentId: 'EXP-001',
      documentNumber: 'EXP-2024-015',
      documentType: 'Expense Claim',
      documentStatus: 'Approved',
      orgUnitId: 'org-004',
      orgUnitName: 'European Operations Ltd',
      orgUnitPath: 'European Operations Ltd',
      debitAmount: 850.25,
      creditAmount: null,
      currencyCode: 'EUR',
      displayPrecision: 2,
      glCode: '5200.3100.002',
      userName: 'Maria Rodriguez',
      ledgerState: 'Active',
      ledgerEntryType: 'Document',
      createdBy: 'Maria Rodriguez',
      sourceSystem: 'Rapid',
      reasonOrNotes: 'Travel expenses - client meeting'
    }, {
      ledgerEntryId: 'OLE-2024-004',
      createdDate: '2024-01-18T11:45:00Z',
      documentId: 'PAY-001',
      documentNumber: 'PAY-2024-001',
      documentType: 'Payment',
      documentStatus: 'Paid',
      orgUnitId: 'org-002',
      orgUnitName: 'New York Headquarters',
      orgUnitPath: 'Global Technologies Inc. > New York Headquarters',
      debitAmount: null,
      creditAmount: 1250.75,
      currencyCode: 'USD',
      displayPrecision: 2,
      glCode: '1100.1000.001',
      vendorName: 'Office Depot',
      ledgerState: 'Active',
      ledgerEntryType: 'Payment',
      createdBy: 'System',
      sourceSystem: 'ERP',
      erpReference: 'ERP-PAY-20240118-001',
      reasonOrNotes: 'Payment for INV-2024-001'
    }, {
      ledgerEntryId: 'OLE-2024-005',
      createdDate: '2024-01-10T08:00:00Z',
      documentId: 'INV-002',
      documentNumber: 'INV-2024-002',
      documentType: 'Invoice',
      documentStatus: 'Draft',
      orgUnitId: 'org-005',
      orgUnitName: 'Finance Department',
      orgUnitPath: 'Global Technologies Inc. > New York Headquarters > Finance Department',
      debitAmount: 750.0,
      creditAmount: null,
      currencyCode: 'USD',
      displayPrecision: 2,
      glCode: '5400.4100.003',
      vendorName: 'Acme Consulting',
      ledgerState: 'Informational',
      ledgerEntryType: 'Document',
      createdBy: 'Robert Johnson',
      sourceSystem: 'Rapid',
      reasonOrNotes: 'Consulting services - draft invoice'
    }, {
      ledgerEntryId: 'OLE-2024-006',
      createdDate: '2024-01-12T16:30:00Z',
      documentId: 'INV-003',
      documentNumber: 'INV-2024-003',
      documentType: 'Invoice',
      documentStatus: 'Cancelled',
      orgUnitId: 'org-003',
      orgUnitName: 'San Francisco Office',
      orgUnitPath: 'Global Technologies Inc. > San Francisco Office',
      debitAmount: 325.5,
      creditAmount: null,
      currencyCode: 'USD',
      displayPrecision: 2,
      glCode: '5100.2200.001',
      vendorName: 'Metro Supplies',
      ledgerState: 'Reversed',
      ledgerEntryType: 'Document',
      createdBy: 'Sarah Williams',
      sourceSystem: 'Rapid',
      reasonOrNotes: 'Incorrect invoice - cancelled'
    }, {
      ledgerEntryId: 'OLE-2024-007',
      createdDate: '2024-01-12T16:35:00Z',
      documentId: 'REV-001',
      documentNumber: 'REV-2024-001',
      documentType: 'Invoice',
      documentStatus: 'Reversed',
      orgUnitId: 'org-003',
      orgUnitName: 'San Francisco Office',
      orgUnitPath: 'Global Technologies Inc. > San Francisco Office',
      debitAmount: null,
      creditAmount: 325.5,
      currencyCode: 'USD',
      displayPrecision: 2,
      glCode: '5100.2200.001',
      vendorName: 'Metro Supplies',
      ledgerState: 'Reversed',
      ledgerEntryType: 'Reversal',
      reversesLedgerEntryId: 'OLE-2024-006',
      createdBy: 'Sarah Williams',
      sourceSystem: 'Rapid',
      reasonOrNotes: 'Reversal of INV-2024-003 - incorrect invoice'
    }, {
      ledgerEntryId: 'OLE-2024-008',
      createdDate: '2024-01-20T13:00:00Z',
      documentId: 'CN-001',
      documentNumber: 'CN-2024-001',
      documentType: 'Credit Note',
      documentStatus: 'Approved',
      orgUnitId: 'org-002',
      orgUnitName: 'New York Headquarters',
      orgUnitPath: 'Global Technologies Inc. > New York Headquarters',
      debitAmount: null,
      creditAmount: 150.0,
      currencyCode: 'USD',
      displayPrecision: 2,
      glCode: '5000.1100.001',
      vendorName: 'Office Depot',
      ledgerState: 'Active',
      ledgerEntryType: 'Document',
      createdBy: 'John Smith',
      sourceSystem: 'Rapid',
      reasonOrNotes: 'Credit for returned items'
    }];
    // Calculate running balance
    let runningBalance = 0;
    const entriesWithBalance = sampleData.map(entry => {
      runningBalance += (entry.debitAmount || 0) - (entry.creditAmount || 0);
      return {
        ...entry,
        calculatedRunningBalance: runningBalance
      };
    });
    setEntries(entriesWithBalance);
  }, []);
  // Handle selecting an entry
  const handleSelectEntry = (entry: LedgerEntry) => {
    setSelectedEntry(entry);
    setIsDetailOpen(true);
  };
  // Handle closing the detail drawer
  const handleCloseDetail = () => {
    setIsDetailOpen(false);
  };
  // Apply filters to entries
  const filteredEntries = entries.filter(entry => {
    // Date filter
    if (filters.dateFrom && new Date(entry.createdDate) < new Date(filters.dateFrom)) {
      return false;
    }
    if (filters.dateTo && new Date(entry.createdDate) > new Date(filters.dateTo)) {
      return false;
    }
    // Document type filter
    if (filters.documentType.length > 0 && !filters.documentType.includes(entry.documentType)) {
      return false;
    }
    // Document status filter
    if (filters.documentStatus.length > 0 && !filters.documentStatus.includes(entry.documentStatus)) {
      return false;
    }
    // Ledger state filter
    if (filters.ledgerState.length > 0 && !filters.ledgerState.includes(entry.ledgerState)) {
      return false;
    }
    // GL Code filter
    if (filters.glCode && !entry.glCode.includes(filters.glCode)) {
      return false;
    }
    // Vendor or User filter
    if (filters.vendorOrUser) {
      const searchLower = filters.vendorOrUser.toLowerCase();
      const matchesVendor = entry.vendorName?.toLowerCase().includes(searchLower);
      const matchesUser = entry.userName?.toLowerCase().includes(searchLower);
      if (!matchesVendor && !matchesUser) {
        return false;
      }
    }
    // Search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      return entry.documentNumber.toLowerCase().includes(searchLower) || entry.orgUnitName.toLowerCase().includes(searchLower) || entry.glCode.toLowerCase().includes(searchLower) || entry.vendorName && entry.vendorName.toLowerCase().includes(searchLower) || entry.userName && entry.userName.toLowerCase().includes(searchLower) || entry.reasonOrNotes && entry.reasonOrNotes.toLowerCase().includes(searchLower);
    }
    return true;
  });
  // Calculate totals for the filtered entries
  const totalDebits = filteredEntries.reduce((sum, entry) => sum + (entry.debitAmount || 0), 0);
  const totalCredits = filteredEntries.reduce((sum, entry) => sum + (entry.creditAmount || 0), 0);
  const currentBalance = totalDebits - totalCredits;
  // Handle filter changes
  const handleFilterChange = (name: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      documentType: [],
      documentStatus: [],
      ledgerState: [],
      orgUnitId: '',
      glCode: '',
      vendorOrUser: '',
      searchTerm: ''
    });
  };
  // Toggle a multi-select filter
  const toggleFilter = (filterName: 'documentType' | 'documentStatus' | 'ledgerState', value: any) => {
    setFilters(prev => {
      const currentValues = prev[filterName];
      return {
        ...prev,
        [filterName]: currentValues.includes(value) ? currentValues.filter(v => v !== value) : [...currentValues, value]
      };
    });
  };
  return <div className="space-y-6" data-id="element-10555">
      <div className="mb-4" data-id="element-10556">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-10557">Operational Ledger</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-10558">
          Read-only financial timeline showing how documents impact financial
          exposure over time
        </p>
      </div>

      {/* Info Panel */}
      {showInfoPanel && <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6" data-id="element-10559">
          <div className="flex" data-id="element-10560">
            <div className="flex-shrink-0" data-id="element-10561">
              <InfoIcon className="h-5 w-5 text-blue-400" data-id="element-10562" />
            </div>
            <div className="ml-3 flex-1" data-id="element-10563">
              <h3 className="text-sm font-medium text-blue-800" data-id="element-10564">
                About the Operational Ledger
              </h3>
              <div className="mt-2 text-sm text-blue-700" data-id="element-10565">
                <p className="mb-2" data-id="element-10566">
                  This ledger is a read-only financial timeline. Every document
                  creates an entry from creation. Financial impact is controlled
                  by document state. Amounts are never edited - corrections
                  appear as reversing entries.
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-10567">
                  <li data-id="element-10568">
                    Append-only: entries are time-ordered and never modified
                  </li>
                  <li data-id="element-10569">Debit increases exposure, Credit reduces exposure</li>
                  <li data-id="element-10570">Balance is calculated, not stored</li>
                  <li data-id="element-10571">Draft entries are informational only</li>
                  <li data-id="element-10572">Not a general ledger - does not replace your ERP</li>
                </ul>
              </div>
            </div>
            <div className="ml-3 flex-shrink-0" data-id="element-10573">
              <button onClick={() => setShowInfoPanel(false)} className="inline-flex text-blue-400 hover:text-blue-600" data-id="element-10574">
                <XIcon className="h-5 w-5" data-id="element-10575" />
              </button>
            </div>
          </div>
        </div>}

      {/* Breadcrumbs */}
      <nav className="flex mb-4" aria-label="Breadcrumb" data-id="element-10576">
        <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-10577">
          <li className="inline-flex items-center" data-id="element-10578">
            <button className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800" data-id="element-10579">
              <BookOpenIcon className="w-4 h-4 mr-2" data-id="element-10580" />
              Financial
            </button>
          </li>
          <li data-id="element-10581">
            <div className="flex items-center" data-id="element-10582">
              <ChevronRightIcon className="w-4 h-4 text-gray-400 mx-1" data-id="element-10583" />
              <span className="text-sm font-medium text-gray-500" data-id="element-10584">
                Operational Ledger
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6" data-id="element-10585">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-10586">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-10587">
            <DatabaseIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-10588" />
            Ledger Entries
          </h3>
          <div className="flex items-center space-x-2" data-id="element-10589">
            <div className="relative rounded-md shadow-sm" data-id="element-10590">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10591">
                <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-10592" />
              </div>
              <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search entries..." value={filters.searchTerm} onChange={e => handleFilterChange('searchTerm', e.target.value)} data-id="element-10593" />
            </div>
            <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10594">
              <FilterIcon className="h-4 w-4 mr-1" data-id="element-10595" />
              Filters
            </button>
            <button onClick={clearFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10596">
              <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-10597" />
              Reset
            </button>
          </div>
        </div>

        {/* Expanded filters */}
        {isFilterOpen && <div className="px-4 py-5 sm:px-6" data-id="element-10598">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-10599">
              {/* Date range */}
              <div className="sm:col-span-2" data-id="element-10600">
                <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700" data-id="element-10601">
                  Date From
                </label>
                <div className="mt-1 relative rounded-md shadow-sm" data-id="element-10602">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10603">
                    <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-10604" />
                  </div>
                  <input type="date" id="dateFrom" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={filters.dateFrom} onChange={e => handleFilterChange('dateFrom', e.target.value)} data-id="element-10605" />
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-10606">
                <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700" data-id="element-10607">
                  Date To
                </label>
                <div className="mt-1 relative rounded-md shadow-sm" data-id="element-10608">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10609">
                    <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-10610" />
                  </div>
                  <input type="date" id="dateTo" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={filters.dateTo} onChange={e => handleFilterChange('dateTo', e.target.value)} data-id="element-10611" />
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-10612">
                <label htmlFor="glCode" className="block text-sm font-medium text-gray-700" data-id="element-10613">
                  GL Code
                </label>
                <div className="mt-1" data-id="element-10614">
                  <input type="text" id="glCode" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="e.g. 5000.1100" value={filters.glCode} onChange={e => handleFilterChange('glCode', e.target.value)} data-id="element-10615" />
                </div>
              </div>

              {/* Document Type */}
              <div className="sm:col-span-3" data-id="element-10616">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10617">
                  Document Type
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-10618">
                  {['Invoice', 'Purchase Order', 'Credit Note', 'Expense Claim', 'Contract', 'Payment'].map(type => <button key={type} type="button" onClick={() => toggleFilter('documentType', type)} className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded ${filters.documentType.includes(type as DocumentType) ? 'bg-indigo-100 border-indigo-300 text-indigo-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-10619">
                      {type}
                    </button>)}
                </div>
              </div>

              {/* Document Status */}
              <div className="sm:col-span-3" data-id="element-10620">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10621">
                  Document Status
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-10622">
                  {['Draft', 'Approved', 'Exported', 'Paid', 'Cancelled', 'Reversed'].map(status => <button key={status} type="button" onClick={() => toggleFilter('documentStatus', status)} className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded ${filters.documentStatus.includes(status as DocumentStatus) ? 'bg-indigo-100 border-indigo-300 text-indigo-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-10623">
                      {status}
                    </button>)}
                </div>
              </div>

              {/* Ledger State */}
              <div className="sm:col-span-2" data-id="element-10624">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10625">
                  Ledger State
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-10626">
                  {['Informational', 'Active', 'Reversed'].map(state => <button key={state} type="button" onClick={() => toggleFilter('ledgerState', state)} className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded ${filters.ledgerState.includes(state as LedgerState) ? 'bg-indigo-100 border-indigo-300 text-indigo-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-10627">
                      {state}
                    </button>)}
                </div>
              </div>

              {/* Vendor or User */}
              <div className="sm:col-span-2" data-id="element-10628">
                <label htmlFor="vendorOrUser" className="block text-sm font-medium text-gray-700" data-id="element-10629">
                  Vendor / User
                </label>
                <input type="text" id="vendorOrUser" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Search vendor or user..." value={filters.vendorOrUser} onChange={e => handleFilterChange('vendorOrUser', e.target.value)} data-id="element-10630" />
              </div>
            </div>
          </div>}
      </div>

      {/* Summary panel */}
      <div className="bg-white shadow rounded-lg p-4 mb-6" data-id="element-10631">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10632">
          <div className="p-4 bg-green-50 rounded-lg border border-green-200" data-id="element-10633">
            <div className="flex justify-between items-center mb-1" data-id="element-10634">
              <h4 className="text-sm font-medium text-green-800" data-id="element-10635">
                Total Debits
              </h4>
              <span className="text-xs text-green-600 font-medium" data-id="element-10636">
                Increases Exposure
              </span>
            </div>
            <p className="text-2xl font-mono font-semibold text-green-700" data-id="element-10637">
              {formatAmount(totalDebits, locale)}
            </p>
            <p className="text-xs text-green-600 mt-1" data-id="element-10638">
              {filteredEntries.filter(e => e.debitAmount).length} entries
            </p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-200" data-id="element-10639">
            <div className="flex justify-between items-center mb-1" data-id="element-10640">
              <h4 className="text-sm font-medium text-red-800" data-id="element-10641">
                Total Credits
              </h4>
              <span className="text-xs text-red-600 font-medium" data-id="element-10642">
                Reduces Exposure
              </span>
            </div>
            <p className="text-2xl font-mono font-semibold text-red-700" data-id="element-10643">
              {formatAmount(totalCredits, locale)}
            </p>
            <p className="text-xs text-red-600 mt-1" data-id="element-10644">
              {filteredEntries.filter(e => e.creditAmount).length} entries
            </p>
          </div>
          <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200" data-id="element-10645">
            <div className="flex justify-between items-center mb-1" data-id="element-10646">
              <h4 className="text-sm font-medium text-indigo-800" data-id="element-10647">
                Current Balance
              </h4>
              <span className="text-xs text-indigo-600 font-medium" data-id="element-10648">
                Calculated
              </span>
            </div>
            <p className="text-2xl font-mono font-semibold text-indigo-700" data-id="element-10649">
              {formatAmount(currentBalance, locale)}
            </p>
            <p className="text-xs text-indigo-600 mt-1" data-id="element-10650">DR - CR = Balance</p>
          </div>
        </div>
      </div>

      {/* Ledger table */}
      <LedgerTable entries={filteredEntries} onSelectEntry={handleSelectEntry} locale={locale} data-id="element-10651" />

      {/* Entry detail drawer */}
      {isDetailOpen && selectedEntry && <LedgerEntryDetail entry={selectedEntry} onClose={handleCloseDetail} locale={locale} data-id="element-10652" />}
    </div>;
}